import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Bell, BellOff, X, ShieldCheck, ShieldAlert, MapPin, ShoppingCart,
  DollarSign, MessageSquare,
} from 'lucide-react';
import useAdminLoginFeed from '../hooks/useAdminLoginFeed';
import { playForEvent, DEFAULT_SOUND_FOR } from '../utils/notifySounds';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LS_ENABLED = 'auto-remap.adminLiveFeed.enabled';

const flagOf = (cc) => {
  if (!cc || cc.length !== 2) return '';
  const A = 0x1F1E6;
  return String.fromCodePoint(A + cc.toUpperCase().charCodeAt(0) - 65)
       + String.fromCodePoint(A + cc.toUpperCase().charCodeAt(1) - 65);
};

const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
  catch (_) { return ''; }
};
const fmtMoney = (n, ccy = 'EUR') => {
  try { return new Intl.NumberFormat('en-EU', { style: 'currency', currency: ccy, maximumFractionDigits: 2 }).format(Number(n) || 0); }
  catch (_) { return `€${(Number(n) || 0).toFixed(2)}`; }
};

/**
 * LiveLoginFeed — top-right toast stack + floating master bell.
 *
 * Sound presets are chosen in Settings → Sounds (server-side, admin-wide).
 * This component fetches the current map on mount and refreshes it on
 * focus so admin's preset changes propagate instantly to every open tab.
 */
export default function LiveLoginFeed({ token }) {
  const [enabled, setEnabled] = useState(() => {
    try { const v = window.localStorage.getItem(LS_ENABLED); return v === null ? true : v === '1'; }
    catch (_) { return true; }
  });
  const [toasts, setToasts] = useState([]);
  const [unseenWhileMuted, setUnseenWhileMuted] = useState(0);
  const [soundMap, setSoundMap] = useState(DEFAULT_SOUND_FOR);
  const timersRef = useRef({});

  // Persist master toggle.
  useEffect(() => {
    try { window.localStorage.setItem(LS_ENABLED, enabled ? '1' : '0'); } catch (_) { /* noop */ }
    if (enabled) setUnseenWhileMuted(0);
  }, [enabled]);

  // Cross-tab sync of master toggle (storage event).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === LS_ENABLED) setEnabled(e.newValue === '1');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Fetch current preset map from server.
  const loadSounds = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/config/sounds`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return;
      const j = await r.json();
      if (j && j.events) setSoundMap({ ...DEFAULT_SOUND_FOR, ...j.events });
    } catch (_) { /* fallback to defaults */ }
  }, [token]);
  useEffect(() => {
    loadSounds();
    const onFocus = () => loadSounds();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadSounds]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
  }, []);

  const pushToast = useCallback((kind, payload, life = 8000) => {
    if (!enabled) {
      setUnseenWhileMuted((n) => n + 1);
      return null;
    }
    const id = `${kind}-${payload.id || Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setToasts((prev) => [{ id, kind, payload }, ...prev].slice(0, 5));
    timersRef.current[id] = setTimeout(() => dismiss(id), life);
    return id;
  }, [enabled, dismiss]);

  // Rolling window of recent FAILED login timestamps (ms). When 5+ fall
  // inside a 60-second window we treat it as a brute-force burst → louder
  // alarm + a separate toast. A 60-second cooldown prevents the alarm
  // from re-firing on every failed attempt during a sustained attack.
  const failedLoginsRef = useRef([]);
  const lastBruteAlarmAtRef = useRef(0);
  const BRUTE_WINDOW_MS    = 60_000;
  const BRUTE_THRESHOLD    = 5;
  const BRUTE_COOLDOWN_MS  = 60_000;

  // Channel handlers — each plays the admin-chosen preset for its event.
  const onLoginEvent = useCallback((payload) => {
    if (!payload) return;
    const id = pushToast('login', payload, payload.success ? 8000 : 12000);
    if (id && !payload.success) {
      playForEvent('login_failed', soundMap);
      // Track failed attempt for brute-force detection.
      const now = Date.now();
      const arr = failedLoginsRef.current;
      arr.push(now);
      // Drop entries older than the window so the count is sliding.
      while (arr.length && now - arr[0] > BRUTE_WINDOW_MS) arr.shift();
      if (arr.length >= BRUTE_THRESHOLD && now - lastBruteAlarmAtRef.current > BRUTE_COOLDOWN_MS) {
        lastBruteAlarmAtRef.current = now;
        playForEvent('login_brute', soundMap);
        // Distinct red alarm toast — surfaces the attack count + window.
        pushToast('brute', {
          id: `brute-${now}`,
          count:  arr.length,
          window: Math.round(BRUTE_WINDOW_MS / 1000),
          email:  payload.email || payload.user_email || null,
          ip:     payload.ip || payload.remote_ip || null,
        }, 20000);
      }
    }
  }, [pushToast, soundMap]);

  const onOrder = useCallback((payload) => {
    if (!payload) return;
    if (pushToast('order', payload, 10000)) playForEvent('order_new', soundMap);
  }, [pushToast, soundMap]);

  const onRecharge = useCallback((payload) => {
    if (!payload) return;
    if (pushToast('recharge', payload, 10000)) playForEvent('recharge_new', soundMap);
  }, [pushToast, soundMap]);

  const onTicketMessage = useCallback((payload) => {
    if (!payload) return;
    if (pushToast('ticket', payload, 10000)) playForEvent('ticket_message', soundMap);
  }, [pushToast, soundMap]);

  useEffect(() => () => {
    Object.values(timersRef.current).forEach(clearTimeout);
    timersRef.current = {};
  }, []);

  const { connected } = useAdminLoginFeed({
    token, enabled, onEvent: onLoginEvent, onOrder, onRecharge, onTicketMessage,
  });

  return (
    <>
      {/* Master mute bell — same as before. Sound preset picker now lives in
          Settings → Sounds (less floating UI = cleaner screen). */}
      <button
        type="button"
        onClick={() => setEnabled((v) => !v)}
        data-testid="live-feed-toggle"
        title={enabled ? 'Mute live feed' : 'Enable live feed'}
        className={`fixed z-[60] bottom-16 right-4 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105 ${
          enabled ? 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white' : 'bg-slate-700 text-slate-300'
        }`}
      >
        {enabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
        {enabled && connected && (
          <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-emerald-400 rounded-full ring-2 ring-white animate-pulse" />
        )}
        {!enabled && unseenWhileMuted > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
            {unseenWhileMuted > 99 ? '99+' : unseenWhileMuted}
          </span>
        )}
      </button>

      {enabled && toasts.length > 0 && (
        <div data-testid="live-feed-stack"
             className="fixed z-[55] top-3 right-3 w-[340px] max-w-[calc(100vw-1.5rem)] flex flex-col gap-2 pointer-events-none">
          {toasts.map((t) => <Toast key={t.id} t={t} onClose={() => dismiss(t.id)} />)}
        </div>
      )}
    </>
  );
}

function Toast({ t, onClose }) {
  const e = t.payload || {};

  if (t.kind === 'order') {
    const openOrder = () => {
      try { window.dispatchEvent(new CustomEvent('admin:open-order', { detail: { orderId: e.id } })); }
      catch (_) { /* noop */ }
      onClose();
    };
    return (
      <div data-testid={`live-feed-toast-${t.id}`} className="pointer-events-auto bg-white rounded-xl shadow-xl border-l-4 border-emerald-500 overflow-hidden animate-in slide-in-from-right-5 fade-in">
        <div className="p-3 flex items-start gap-2">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <ShoppingCart className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-1">
              <p className="text-sm font-bold text-slate-800 truncate">New order #{e.order_number || (e.id ? e.id.slice(0,8) : '')}</p>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5" /></button>
            </div>
            <p className="text-[11px] text-slate-500 truncate mt-0.5">
              {[e.first_name, e.surname].filter(Boolean).join(' ') || e.user_email}
              {e.account_id && <span className="ml-1 font-mono text-slate-400">#{e.account_id}</span>}
            </p>
            <p className="text-[12px] text-slate-700 truncate mt-1">{[e.manufacturer, e.model, e.engine].filter(Boolean).join(' · ') || '—'}</p>
            <div className="flex items-center justify-between mt-1.5 text-[11px]">
              <span className="font-bold text-emerald-700 tabular-nums">{fmtMoney(e.total_price, e.currency)}</span>
              <span className="font-mono text-slate-400 tabular-nums">{fmtTime(e.created_at)}</span>
            </div>
            {e.id && (
              <button
                type="button"
                onClick={openOrder}
                data-testid={`live-feed-open-order-${e.id}`}
                className="mt-2 w-full px-2 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold tracking-wide uppercase transition"
              >
                Open order
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (t.kind === 'recharge') {
    return (
      <div data-testid={`live-feed-toast-${t.id}`} className="pointer-events-auto bg-white rounded-xl shadow-xl border-l-4 border-amber-500 overflow-hidden animate-in slide-in-from-right-5 fade-in">
        <div className="p-3 flex items-start gap-2">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
            <DollarSign className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-1">
              <p className="text-sm font-bold text-slate-800 truncate">
                Recharge {fmtMoney(e.amount, e.currency)}
                <span className="ml-1.5 text-[10px] font-mono uppercase text-amber-600 bg-amber-50 px-1 rounded">{e.method || ''}</span>
              </p>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5" /></button>
            </div>
            <p className="text-[11px] text-slate-500 truncate mt-0.5">{[e.first_name, e.surname].filter(Boolean).join(' ') || e.user_email}</p>
            {(e.bonus > 0 || e.welcome_bonus) && (
              <p className="text-[11px] text-amber-700 mt-1">
                + bonus {fmtMoney((e.welcome_bonus?.amount || 0) + (e.bonus || 0), e.currency)} → total {fmtMoney(e.credited + (e.welcome_bonus?.amount || 0), e.currency)}
              </p>
            )}
            <p className="text-[10px] font-mono text-slate-400 mt-1 truncate">{fmtTime(e.created_at)}</p>
          </div>
        </div>
      </div>
    );
  }

  if (t.kind === 'brute') {
    return (
      <div
        data-testid={`live-feed-toast-${t.id}`}
        className="pointer-events-auto bg-white rounded-xl shadow-2xl border-l-4 border-rose-600 overflow-hidden animate-in slide-in-from-right-5 fade-in ring-2 ring-rose-300/50"
      >
        <div className="p-3 flex items-start gap-2 bg-gradient-to-r from-rose-50 to-white">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-rose-600 text-white flex items-center justify-center animate-pulse">
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-1">
              <p className="text-sm font-extrabold text-rose-700 truncate uppercase tracking-wide">
                Brute-force alert
              </p>
              <button onClick={onClose} className="text-rose-400 hover:text-rose-600"><X className="w-3.5 h-3.5" /></button>
            </div>
            <p className="text-[12px] text-slate-700 mt-0.5">
              <span className="font-bold text-rose-700">{e.count} failed logins</span> in the last {e.window}s
            </p>
            {(e.email || e.ip) && (
              <p className="text-[11px] text-slate-500 truncate mt-1">
                {e.email && <span className="font-mono">{e.email}</span>}
                {e.email && e.ip && <span className="mx-1">·</span>}
                {e.ip && <span className="font-mono">{e.ip}</span>}
              </p>
            )}
            <p className="text-[10px] text-rose-600 mt-1 italic">Check IP firewall &amp; recent login feed.</p>
          </div>
        </div>
      </div>
    );
  }

  if (t.kind === 'ticket') {
    const openTicketOrder = () => {
      if (!e.order_id) return;
      try { window.dispatchEvent(new CustomEvent('admin:open-order', { detail: { orderId: e.order_id } })); }
      catch (_) { /* noop */ }
      onClose();
    };
    return (
      <div data-testid={`live-feed-toast-${t.id}`} className="pointer-events-auto bg-white rounded-xl shadow-xl border-l-4 border-violet-500 overflow-hidden animate-in slide-in-from-right-5 fade-in">
        <div className="p-3 flex items-start gap-2">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center">
            <MessageSquare className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-1">
              <p className="text-sm font-bold text-slate-800 truncate">Ticket #{e.order_number || (e.order_id ? e.order_id.slice(0,8) : '')}</p>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5" /></button>
            </div>
            <p className="text-[11px] text-slate-500 truncate mt-0.5">{[e.first_name, e.surname].filter(Boolean).join(' ') || e.user_email}</p>
            <p className="text-[12px] text-slate-700 mt-1 line-clamp-2">{e.text}</p>
            <p className="text-[10px] font-mono text-slate-400 mt-1 truncate">{e.vehicle ? `${e.vehicle} · ` : ''}{fmtTime(e.created_at)}</p>
            {e.order_id && (
              <button
                type="button"
                onClick={openTicketOrder}
                data-testid={`live-feed-open-ticket-${e.order_id}`}
                className="mt-2 w-full px-2 py-1.5 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-[11px] font-bold tracking-wide uppercase transition"
              >
                Open ticket
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // login (default)
  const ok = !!e.success;
  return (
    <div data-testid={`live-feed-toast-${t.id}`} className={`pointer-events-auto bg-white rounded-xl shadow-xl border-l-4 ${ok ? 'border-emerald-500' : 'border-rose-500'} overflow-hidden animate-in slide-in-from-right-5 fade-in`}>
      <div className="p-3 flex items-start gap-2">
        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
          {ok ? <ShieldCheck className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1">
            <p className="text-sm font-bold text-slate-800 truncate">
              {[e.first_name, e.surname].filter(Boolean).join(' ') || e.user_email || 'Anonymous'}
              {e.account_id && <span className="ml-1 text-[10px] font-mono text-slate-400">#{e.account_id}</span>}
            </p>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5" /></button>
          </div>
          <p className="text-[11px] text-slate-500 truncate mt-0.5">{e.user_email}</p>
          <div className="flex items-center gap-2 mt-1.5 text-[11px]">
            {e.country_code && <span className="text-base">{flagOf(e.country_code)}</span>}
            <span className="text-slate-600 truncate flex-1">
              <MapPin className="w-3 h-3 inline -mt-0.5 opacity-60" /> {e.city || e.country || 'Unknown'}
            </span>
            <span className="font-mono text-slate-500 tabular-nums">{fmtTime(e.created_at)}</span>
          </div>
          <p className="text-[10px] font-mono text-slate-400 mt-1 truncate">
            {e.ip}{e.device_type ? ` · ${e.device_type}` : ''}{e.browser ? ` · ${e.browser}` : ''}
            {!ok && e.reason ? ` · ${(e.reason || '').replaceAll('_', ' ')}` : ''}
          </p>
        </div>
      </div>
    </div>
  );
}
